import { useAppDispatch } from 'app/hooks';
import { useNavigate } from 'react-router-dom';
import { useAddPlaceMutation } from '../services';
import { useAddDocumentMutation, useDeleteDocumentMutation } from 'services/documents';
import { FETCH_ALL_PARAMS } from 'constants/query';
import { useGetTerminalsQuery } from 'features/terminal/services';
import { useForm } from 'hooks/useForm';
import { PLACE_FIELDS_SCHEMA, PLACE_INITIAL_FIELDS } from '../constants/place-fields';
import { FormEvent, useCallback } from 'react';
import { setErrorMessage, setSuccessMessage } from 'store/toast-slice';
import { ServerErrorType } from 'types/server-responses';
import { removeEmpty, trimValues } from 'utils/object-helpers';
import { PlaceDto } from '../types/dtos';
import { dataURLtoFile } from 'utils/file-helpers';
import { useGetCitiesQuery } from 'features/city/service';
import { useWorkspace } from 'hooks/useWorkspace';
import { setWorkspace } from 'store/workspace-slice';

const useController = () => {
    const { websiteId, websiteName, placeId } = useWorkspace();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [addPlace, { isLoading: isAddPlaceLoading }] = useAddPlaceMutation();
    const [addDocument, { isLoading: isAddDocumentLoading }] = useAddDocumentMutation();
    const [deleteDocument, { isLoading: isDeleteDocumentLoading }] = useDeleteDocumentMutation();
    const { data: terminals, isLoading: isGetTerminalsLoading } = useGetTerminalsQuery(FETCH_ALL_PARAMS);
    const { data: cities, isLoading: isGetCitiesLoading } = useGetCitiesQuery(FETCH_ALL_PARAMS);
    const {
        formData,
        handleChange: onChange,
        handleSubmit,
        errors,
    } = useForm({
        defaultValues: PLACE_INITIAL_FIELDS,
        validationSchema: PLACE_FIELDS_SCHEMA,
    });

    const handleSuccess = useCallback(() => {
        dispatch(setSuccessMessage({ key: 'place.PLACE_ADDED' }));
    }, [dispatch]);

    const handleErrors = useCallback(
        (error: ServerErrorType) => {
            const code = error.data?.errorCode;
            dispatch(setErrorMessage({ key: code }));
        },
        [dispatch],
    );

    const getPayload = useCallback(
        (documentId?: string) => {
            const data = { ...formData };
            if (documentId) data.documentId = documentId;
            delete data.logo;
            data.websiteId = websiteId;
            data.minimumOrderAmount = Number(data.minimumOrderAmount);
            data.longitude = Number(data.longitude);
            data.latitude = Number(data.latitude);
            return trimValues(removeEmpty(data)) as PlaceDto;
        },
        [formData, websiteId],
    );

    const onSubmit = useCallback(
        (e: FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            handleSubmit(async () => {
                let documentId: string | undefined;
                try {
                    if (formData?.logo)
                        documentId = (await addDocument(dataURLtoFile(formData.logo)).unwrap()).documentId;

                    const payload = getPayload(documentId);
                    const place = await addPlace(payload).unwrap();
                    console.log(place);

                    handleSuccess();
                    if (placeId === '' && place?.id) {
                        console.log('Setting workspace with placeId:', place.id);

                        dispatch(setWorkspace({ placeId: place.id }));
                    }
                    navigate('../places');
                } catch (error) {
                    documentId && (await deleteDocument(documentId).unwrap());
                    handleErrors(error as ServerErrorType);
                }
            });
        },
        [
            addPlace,
            addDocument,
            deleteDocument,
            formData?.logo,
            getPayload,
            handleErrors,
            handleSubmit,
            handleSuccess,
            navigate,
            placeId,
            dispatch,
        ],
    );

    const onCancel = useCallback(() => {
        navigate('../places');
    }, [navigate]);

    return {
        formData,
        terminals,
        website: { id: websiteId, name: websiteName },
        cities,
        onSubmit,
        onCancel,
        onChange,
        isLoading: isGetTerminalsLoading || isGetCitiesLoading,
        isAddPlaceLoading: isAddPlaceLoading || isAddDocumentLoading || isDeleteDocumentLoading,
        errors,
    };
};

export default useController;
